<template>
  <div class="gmf-side-scrolling-comments-wrap">
    <div @click="prevPage" class="gmf-side-scrolling-comments-btn" :class="hasPrevPage ? 'gmf-clickable' : null"><Icon icon="chevron_left" /></div>

    <div class="gmf-side-scrolling-comments-container">
      <div class="gmf-side-scrolling-comments-anchor" :style="anchorStyle">
        <div class="gmf-side-scrolling-comments-page" v-for="(page, index) in pages" :key="index">
          <a v-for="comment in page" :key="comment.id" :href="commentUrl(comment)" @click="ev => onClick(ev, comment)" class="gmf-clickable gmf-side-scrolling-comments-comment-wrap">
            <Comment
              :comment="comment"
              voteMode="none"
              :collapse="true"
              :hideReplies="true"
              :elements="{footer: false, title: false, uploads: false, prosAndCons: false, action: true, productImage: true, targetImage: true, target: false, customerLevel: false, stem: false, animateAvatar: true, translateButton: false}"
              :disableTargetLink="true"
            />
          </a>
        </div>
      </div>
    </div>

    <div @click="nextPage" class="gmf-side-scrolling-comments-btn" :class="hasNextPage ? 'gmf-clickable' : null"><Icon icon="chevron_right" /></div>
  </div>
</template>

<style>
.gmf-side-scrolling-comments-wrap {
  display: flex;
  gap: var(--gmf-p-3);
  align-items: center;
  overflow: hidden;
  max-width: 100%;
}

.gmf-side-scrolling-comments-container {
  overflow: hidden;
}

.gmf-side-scrolling-comments-anchor {
  display: flex;
  transition: transform 0.5s ease-out;
}

.gmf-side-scrolling-comments-page {
    display: flex;
    gap: var(--gmf-p-4);
    width: 100%;
    flex-shrink: 0;
    align-items: center;
}

.gmf-side-scrolling-comments-comment-wrap {
  overflow: hidden;
  width: 100%; /* does not actually set width, but is required for proper sizing */
}

.gmf-side-scrolling-comments-page .gmf-comment-content {
  width: 100%;
}

.gmf-side-scrolling-comments-btn {
  opacity: 0.5;
  padding: var(--gmf-p-2);
}
.gmf-side-scrolling-comments-btn.gmf-clickable {
  opacity: 1;
}

</style>

<script>
import Comment from '@/components/Comment';
import Icon from '@/components/Icon';

import callApi from '@/utils/callApi';
import elSize from '@/utils/mixins/elSize';

import openSocialModal from '@/widgets/SocialFeed/openSocialModal';
import state from '@/state';


export default {
  mixins: [elSize],
  props: ['compactMobileView', 'commentType'],
  components: {
    Comment,
    Icon,
  },
  data () {
    return {
      comments: [],
      page: 1,
    };
  },
  methods: {
    t: state.t,
    nextPage () {
      this.page = Math.min(this.page + 1, this.pages.length);
    },
    prevPage () {
      this.page = Math.max(this.page - 1, 1);
    },
    isGoodComment (comment) {
      if (comment.content.images && comment.content.images[0]) {
        return true;
      }
      if (comment.type && comment.type.name === 'site_review' && comment.content.text) {
        return true;
      }
      return (comment.target && comment.target.imageUrl);
    },
    onClick (ev, comment) {
      ev.stopPropagation();
      ev.preventDefault();

      let filter = null;

      if (this.commentType && this.commentType.split(',').length == 1) {
        filter = {
          type: this.commentType,
        };
      }

      openSocialModal({
        scrollToCommentId: comment ? comment.id : null,
        displayHeader: true,
        filter,
      });
      return false;
    },
    commentUrl (comment) {
      if (comment.type.name === 'social_post' || comment.type.name === 'marketplace' || comment.type.name === 'site_review') {
        return this.socialFeedUrl;
      }
      return comment.target.url + '#gmf-comment-section';
    },
  },
  watch: {
    pages () {
      if (this.page > this.pages.length) {
        this.page = this.pages.length;
      }
    },
  },
  computed: {
    hasPrevPage () {
      return this.page > 1;
    },
    hasNextPage () {
      return this.page < this.pages.length;
    },
    itemsPerPage () {
      if (this.elSize.width < 700) {
        return 1;
      }
      if (this.elSize.width < 1000) {
        return 2;
      }
      return 3;
    },
    anchorStyle () {
      return {
        transform: 'translateX(-' + ((this.page - 1) * 100) + '%)',
      }
    },
    pages () {
      function chunkArray (array, size) {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
          result.push(array.slice(i, i + size));
        }
        return result;
      }

      return chunkArray(this.comments, this.itemsPerPage);
    },
    shouldOpenInSocialModal () {
      const cnf = state.merchantConfig.newCommentsSlideshow;
      return cnf && cnf.linkType === 'social_modal';
    },
    translationsEnabled() {
      return state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    },
    socialFeedUrl() {
      return state.merchantConfig?.widgets?.urls?.socialFeed;
    }
  },
  async mounted () {
    const query = new URLSearchParams({
      'take': 12,
      'include': 'author',
      'order': 'created.desc',
      'translate': this.translationsEnabled,
      'max_age_days': 180,
      'reply_depth': 0,
      'no_count': 1,
    });

    if (this.commentType) {
      const commentTypes = this.commentType.split(',');
      commentTypes.forEach(ct => query.append('type', ct));
    }

    const res = await callApi('GET', state.commentSearchEndpoint + '?'+query.toString());
    const { comments } = await res.json();


    // Simple way to put less focus on "uglier" comments (for example without product image)
    const wantedCount = 12;

    this.comments = [];

    for (const comment of comments) {
      if (this.isGoodComment(comment)) {
        this.comments.push(comment);
      }
      if (this.comments.length >= wantedCount) {
        break;
      }
    }
    for (const comment of comments) {
      if (! this.isGoodComment(comment)) {
        this.comments.push(comment);
      }
      if (this.comments.length >= wantedCount) {
        break;
      }
    }
  },
};
</script>
